import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  distinctUntilChanged,
  Observable,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { RequestStatusesService } from '@core/services/request/request-statuses.service';
import { assertNever } from '@core/helpers/assertNever';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestStatus, RequestStatuses } from '@core/types/RequestStatus';
import { RequestFormStatusesListComponent } from '@features/requests';
import { Nullable } from '@core/helpers/nullable';
import { RequestService } from '@core/services/request/request.service';
import { RequestDTO } from '@core/types';
import { XAlertService } from '@core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { compareById } from '@core/helpers';
import { RequestStatusFormData } from '@features/requests/request-form/components/request-form-status/types';

@Component({
  selector: 'x-request-form-status',
  imports: [AsyncPipe, ReactiveFormsModule, RequestFormStatusesListComponent],
  templateUrl: './request-form-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormStatusComponent implements OnInit {
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly requestService = inject(RequestService);
  private readonly xAlertService = inject(XAlertService);
  private readonly requestStatusesService = inject(RequestStatusesService);
  private readonly destroyRef = inject(DestroyRef);
  readonly document = inject(DOCUMENT);

  $statusChangeInProcess = output<boolean>({
    alias: 'statusChangeInProcess',
  });

  readonly $request = this.requestService.$request;
  readonly formControl = new FormControl<Nullable<RequestStatus>>(
    this.$request().statusId,
  );
  private valueChanges = this.formControl.valueChanges.pipe(
    distinctUntilChanged(compareById),
    tap(() => {
      this.$statusChangeInProcess.emit(true);
    }),
    switchMap(this.saveRequestStatus$.bind(this)),
  );

  public errorHappened$ = new Subject<void>();
  readonly canLogistChangeRequestStatus$ =
    this.permissionService.canLogistChangeRequestStatus$;

  $insideDropdown = input<boolean>(false, { alias: 'insideDropdown' });

  ngOnInit() {
    this.valueChangeSubscription();
    if (this.$insideDropdown()) {
      this.setDynamicStylesForSheet();
    }
  }

  private valueChangeSubscription = () => {
    this.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.formControl.value?.name) {
          this.xAlertService.success(
            'Статус заявки успешно изменен',
            `Текущий статус: <b>${this.formControl.value.name}</b>`,
          );
          this.$statusChangeInProcess.emit(false);
        }
        this.requestService.reloadRequest();
      },
      error: () => {
        // revert old status in UI if error happened and status was not changed
        const request = this.$request();
        const formValue = this.formControl.value;

        this.$statusChangeInProcess.emit(false);

        if (
          request.statusId?.id &&
          formValue?.id &&
          request.statusId.id !== formValue.id
        ) {
          this.errorHappened$.next();
        }
        // если не вызвать подписку заново, то почему-то не срабатывает valueChanges
        // после ошибки при попытке изменить статус на такой же который вызвал ошибку ранее
        this.valueChangeSubscription();
      },
    });
  };

  private saveRequestStatus$(
    status: RequestStatusFormData,
  ): Observable<null | RequestDTO[] | void> {
    switch (status.id) {
      case RequestStatuses.EditQuery:
        return this.requestStatusesService.tryToSetEditQueryStatus$(status);
      case RequestStatuses.Completed:
        return this.requestStatusesService.tryToSetCompletedStatus$(status);
      case RequestStatuses.Given:
        return this.requestStatusesService.tryToSetGivenStatus$(status);
      case RequestStatuses.Cancelled:
        return this.requestStatusesService.tryToSetCancelledStatus$(status);
      case RequestStatuses.New:
      case RequestStatuses.OnEdit:
      case RequestStatuses.CheckFinished:
      case RequestStatuses.RequestIsWaitingForCheck:
      case RequestStatuses.InWork:
      case RequestStatuses.SLSWasAppointed:
      case RequestStatuses.MistakeInRequest:
      case RequestStatuses.RejectedLocal:
      case RequestStatuses.Rejected:
        return this.requestStatusesService.changeRequestStatus$(status.id);
      default:
        assertNever(status.id);
    }
  }

  setDynamicStylesForSheet(): void {
    const dropdown = this.document.querySelector('tui-dropdown');

    if (dropdown) {
      dropdown.classList.add('rounded-[32px]!');
    }
  }
}
